import { styled } from '@linaria/react'
import { ButtonSC } from 'atoms/Button'
import { FIXED_INDENTS, MEDIA } from 'vars/index'

/**
 * `content` of the details page `banner-description` block that follows the
 * banner: the description / directors / stars row with the More Details button
 * aligned to its right edge.
 */
export const DetailsAreaContentSC = styled.div`
  --detailsAreaColumnGap: var(${FIXED_INDENTS.INDENT_120});
  --detailsAreaCaptionGap: var(${FIXED_INDENTS.INDENT_20});

  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-end;

  ${ButtonSC} {
    &[data-arcana-button='true'] {
      min-width: 0;
      padding: var(${FIXED_INDENTS.INDENT_30}) 0;
    }
  }

  ${MEDIA.MAX_1639} {
    --detailsAreaColumnGap: var(${FIXED_INDENTS.INDENT_100});
  }

  ${MEDIA.MAX_1239} {
    --detailsAreaColumnGap: var(${FIXED_INDENTS.INDENT_60});
  }
`

/**
 * From 1024 a concept that can show a people column (`hasPeopleColumn`, the
 * default) splits the row into two equal halves while a description is
 * present: description on the left and directors/stars on the right. The
 * halves are declared as tracks rather than derived from the children, so a
 * title with no directors or stars keeps its description at half width
 * instead of stretching it across the page.
 *
 * When directors/stars render without a description, `hasPeopleColumn` is
 * false for that render so the people pair uses the single full-width track.
 *
 * The season description never renders a people column (season data carries
 * no directors/stars in this concept), so it opts out with
 * `hasPeopleColumn={false}` and keeps the single full-width track its own
 * container already sizes.
 */
export const DetailsAreaTextSC = styled.div<{ hasPeopleColumn?: boolean }>`
  display: grid;
  width: 100%;
  align-items: start;
  column-gap: var(--detailsAreaColumnGap);
  row-gap: var(--detailsAreaColumnGap);
  /* new/body/base tracking: 3% of the 16px body font size */
  letter-spacing: 0.48px;

  ${MEDIA.MIN_1024} {
    grid-template-columns: ${({ hasPeopleColumn = true }) =>
      hasPeopleColumn ? '1fr 1fr' : '1fr'};
  }
`

export const DetailsAreaColumnSC = styled.div`
  display: flex;
  flex: 1 0 0;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  row-gap: var(--detailsAreaCaptionGap);
`

/**
 * Directors and stars stay visible at every width. Below 1024 they occupy the
 * next implicit grid track under the synopsis (or the only track when there
 * is no synopsis). From 1024 the parent two-column grid places this flex
 * pair in the people half.
 */
export const DetailsAreaPeopleSC = styled.div`
  display: flex;
  flex: 1 0 0;
  min-width: 0;
  align-items: flex-start;
  column-gap: var(--detailsAreaColumnGap);
`
